import React, { forwardRef, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CheckCircle, Info, WarningCircle, X } from "@phosphor-icons/react";
import { useLocalization } from "../../localization";
import "./FormErrors.css";

type Props = {
  tone: "error" | "success" | "info";
  children: React.ReactNode;
  /** Changes in feedback identity make a previously dismissed banner visible again. */
  messageKey?: React.Key;
  onDismiss?: () => void;
  /** Errors retain their explanation and recovery actions unless their owner handles dismissal. */
  dismissible?: boolean;
  id?: string;
  tabIndex?: number;
};

/** Keep feedback in a shared viewport stack, inside its dialog's focus boundary when present. */
const FormFeedbackBanner = forwardRef<HTMLDivElement, Props>(function FormFeedbackBanner({
  tone, children, messageKey, onDismiss, dismissible = tone !== "error" || Boolean(onDismiss), id, tabIndex,
}, ref) {
  const { t } = useLocalization();
  const anchor = useRef<HTMLSpanElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const [dismissed, setDismissed] = useState<{ key: React.Key | undefined; tone: Props["tone"] } | null>(null);
  const visible = !dismissed || dismissed.key !== messageKey || dismissed.tone !== tone;
  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!visible) return;
    setDismissed(null);
    returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const source = anchor.current?.parentElement;
    let slot: HTMLDivElement | null = null;
    const removeSlot = () => {
      const stack = slot?.parentElement;
      slot?.remove();
      if (stack && !stack.childElementCount) stack.remove();
      slot = null;
    };
    const sync = () => {
      const parent = source?.closest('[role="dialog"], [role="alertdialog"]')
        || document.fullscreenElement || document.body;
      if (source?.closest('[hidden], [aria-hidden="true"]')) {
        removeSlot();
        setContainer(null);
        return;
      }
      if (slot?.parentElement?.parentElement === parent) return;
      removeSlot();
      let stack = Array.from(parent.children).find(child => child.classList.contains("form-feedback-stack"));
      if (!stack) {
        stack = document.createElement("div");
        if (parent === document.body) stack.id = "form-feedback-stack";
        stack.className = "form-feedback-stack";
        parent.appendChild(stack);
      }
      slot = document.createElement("div");
      slot.className = `form-feedback-slot form-feedback-slot--${tone}`;
      stack.appendChild(slot);
      setContainer(slot);
    };
    // Portals must respect mounted but hidden admin sections and tab panels.
    const observer = new MutationObserver(sync);
    for (let ancestor = source; ancestor; ancestor = ancestor.parentElement) {
      observer.observe(ancestor, { attributes: true, attributeFilter: ["hidden", "aria-hidden"] });
    }
    document.addEventListener("fullscreenchange", sync);
    sync();

    return () => {
      observer.disconnect();
      document.removeEventListener("fullscreenchange", sync);
      removeSlot();
    };
  }, [tone, visible]);

  const dismiss = () => {
    if (container?.contains(document.activeElement) && returnFocus.current?.isConnected) {
      returnFocus.current.focus({ preventScroll: true });
    }
    setDismissed({ key: messageKey, tone });
    onDismiss?.();
  };
  const Icon = tone === "error" ? WarningCircle : tone === "success" ? CheckCircle : Info;

  return <>
    <span ref={anchor} hidden />
    {visible && container ? createPortal(
      <div ref={ref} id={id} tabIndex={tabIndex} className={`form-feedback-banner form-${tone}-banner`}
        role={tone === "error" ? "alert" : "status"}
        aria-live={tone === "error" ? "assertive" : "polite"} aria-atomic="true">
        <Icon className="form-feedback-icon" size={24} weight="fill" aria-hidden="true" />
        <div className="form-feedback-content">{children}</div>
        {dismissible ? <button type="button" className="form-feedback-dismiss" aria-label={t("Dismiss")} onClick={dismiss}>
          <X size={20} aria-hidden="true" />
        </button> : null}
      </div>,
      container
    ) : null}
  </>;
});

export default FormFeedbackBanner;
